import React, { useEffect, useState, useCallback } from "react";
import {
  Newspaper, Sparkles, ChevronRight, Lock, ShieldOff, Wand2, Clock3,
  Check, CreditCard, Bell, FileText, Loader2, ThumbsUp, ThumbsDown,
  ArrowLeft, LogOut, UserRound, X, RefreshCw, ExternalLink, Trash2,
} from "lucide-react";
import { api, formatApiError } from "./lib/api";
import "./index.css";

const STARTER_TOPICS = ["World", "UK", "Technology", "AI", "Business", "Finance",
  "Crypto", "Sport", "Football", "Politics", "Entertainment", "Culture",
  "Film & TV", "Music", "Gaming", "Health", "Science", "Space", "Climate",
  "Environment", "Travel", "Food", "Fashion", "Property", "Education", "Startups"];

const STARTER_OUTLETS = ["BBC News", "Sky News", "ITV News", "Channel 4 News", "GB News",
  "TalkTV", "The Guardian", "The Times", "The Telegraph", "Daily Mail", "The Sun",
  "The Mirror", "The Independent", "Evening Standard", "The Spectator", "New Statesman",
  "Reuters", "Associated Press", "Bloomberg", "Financial Times", "The Economist",
  "Forbes", "CNN", "Fox News", "MSNBC", "New York Times", "Washington Post",
  "The Atlantic", "Politico", "Vice", "BuzzFeed News", "HuffPost", "Breitbart",
  "Daily Wire", "Al Jazeera", "Euronews", "Deutsche Welle"];

const FORMAT_LABELS = { summary: "Summary", bullets: "Bullet points", plain_english: "Plain English" };

function formatBriefDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  return new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(d);
}

function Header({ user, onLogout }) {
  return (
    <header className="sticky top-0 z-30 border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]/85 backdrop-blur-xl" data-testid="header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <div className="flex items-center gap-3">
          <div className="grid size-10 place-items-center rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-editorial">
            <Newspaper className="size-5" />
          </div>
          <div>
            <p className="font-serif text-xl font-semibold">Curate</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">You choose what matters. We find it.</p>
          </div>
        </div>
        {user ? (
          <button data-testid="logout-btn" className="btn btn-quiet px-4 py-2 text-sm" onClick={onLogout}>
            <LogOut className="size-4" /> Log out
          </button>
        ) : (
          <a className="text-sm font-medium hover:text-[hsl(var(--primary))]" href="#access">Sign in</a>
        )}
      </div>
    </header>
  );
}

function PricingCard({ title, price, points, featured, testId }) {
  return (
    <article data-testid={testId} className={featured
      ? "rounded-xl border border-[hsl(var(--primary))] bg-premium p-7 shadow-panel"
      : "rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-7 shadow-soft"}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-2xl font-semibold">{title}</h3>
          <p className="mt-3 font-serif text-4xl font-semibold">{price}</p>
        </div>
        {featured && <CreditCard className="size-6 text-[hsl(var(--primary))]" />}
      </div>
      <ul className="mt-7 space-y-3">
        {points.map((p) => (
          <li key={p} className="flex gap-3 text-[hsl(var(--muted-foreground))]">
            <Check className="mt-1 size-4 shrink-0 text-[hsl(var(--primary))]" /> {p}
          </li>
        ))}
      </ul>
    </article>
  );
}

function ForgotPassword({ presetEmail, onDone, onCancel }) {
  const [stage, setStage] = useState("email"); // email | code
  const [email, setEmail] = useState(presetEmail || "");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const inputs = React.useRef([]);
  const code = digits.join("");

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => prev.map((d, idx) => idx === i ? clean : d));
    if (clean && i < 5) inputs.current[i + 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    setDigits(txt.padEnd(6, "").split("").slice(0, 6));
    inputs.current[Math.min(txt.length, 5)]?.focus();
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) { setMsg("Enter your email."); return; }
    setBusy(true); setMsg("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setStage("code");
      setMsg("If that email is registered, we just sent a 6-digit code.");
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { setMsg("Enter all 6 digits."); return; }
    if (newPassword.length < 6) { setMsg("New password must be at least 6 characters."); return; }
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/auth/reset-password", { email: email.trim(), code, new_password: newPassword });
      if (data.token) localStorage.setItem("curate_token", data.token);
      onDone(data.user);
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen overflow-hidden">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-ambient" />
      <Header user={null} />
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl flex-col items-center justify-center px-5 py-12 sm:px-8">
        <div data-testid="reset-card" className="curate-rise w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-panel">
          <div className="grid size-12 place-items-center rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">
            <Lock className="size-6" />
          </div>
          <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight">
            {stage === "email" ? "Forgot your password?" : "Set a new password"}
          </h1>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">
            {stage === "email"
              ? "Enter your account email and we'll send you a 6-digit reset code."
              : <>We sent a 6-digit code to <span className="font-semibold text-[hsl(var(--foreground))]">{email}</span>. It expires in 15 minutes.</>}
          </p>
          {stage === "email" ? (
            <form onSubmit={sendCode} className="mt-7">
              <label className="block text-sm font-medium" htmlFor="reset-email">Email</label>
              <input id="reset-email" data-testid="reset-email-input" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
                className="mt-2 h-12 w-full rounded-lg border border-[hsl(var(--input))] px-4 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              {msg && <p data-testid="reset-error" className="mt-4 rounded-lg bg-[hsl(var(--muted))] px-4 py-3 text-sm">{msg}</p>}
              <button data-testid="reset-send-code" type="submit" disabled={busy} className="btn btn-hero mt-6 w-full justify-center px-6 py-4 text-lg">
                {busy ? <Loader2 className="size-5 animate-spin" /> : "Send reset code"}
              </button>
            </form>
          ) : (
            <form onSubmit={submitReset} className="mt-7">
              <label className="block text-sm font-medium">6-digit code</label>
              <div className="mt-2 flex gap-2 sm:gap-3" onPaste={onPaste}>
                {digits.map((d, i) => (
                  <input key={i} data-testid={`reset-digit-${i}`} ref={(el) => (inputs.current[i] = el)}
                    value={d} onChange={(e)=>setDigit(i, e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={1}
                    className="h-14 w-full flex-1 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--card))] text-center font-serif text-3xl font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
                ))}
              </div>
              <label className="mt-5 block text-sm font-medium" htmlFor="new-password">New password</label>
              <input id="new-password" data-testid="reset-new-password" type="password" minLength={6} required value={newPassword} onChange={(e)=>setNewPassword(e.target.value)}
                className="mt-2 h-12 w-full rounded-lg border border-[hsl(var(--input))] px-4 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              {msg && <p data-testid="reset-error" className="mt-4 rounded-lg bg-[hsl(var(--muted))] px-4 py-3 text-sm">{msg}</p>}
              <button data-testid="reset-submit" type="submit" disabled={busy || code.length !== 6 || newPassword.length < 6} className="btn btn-hero mt-6 w-full justify-center px-6 py-4 text-lg">
                {busy ? <Loader2 className="size-5 animate-spin" /> : "Reset password"}
              </button>
              <button data-testid="reset-resend" type="button" onClick={sendCode} className="mt-4 w-full text-sm font-medium text-[hsl(var(--primary))] hover:underline">Send a new code</button>
            </form>
          )}
          <div className="mt-6 flex justify-end text-sm">
            <button data-testid="reset-cancel" type="button" onClick={onCancel} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">Back to log in</button>
          </div>
        </div>
      </section>
    </main>
  );
}

function VerifyEmail({ email, onVerified, onCancel }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const inputs = React.useRef([]);

  const code = digits.join("");

  const setDigit = (i, v) => {
    const clean = v.replace(/\D/g, "").slice(0, 1);
    setDigits((prev) => prev.map((d, idx) => idx === i ? clean : d));
    if (clean && i < 5) inputs.current[i + 1]?.focus();
  };
  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputs.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const next = txt.padEnd(6, "").split("").slice(0, 6);
    setDigits(next);
    inputs.current[Math.min(txt.length, 5)]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) { setMsg("Enter all 6 digits."); return; }
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/auth/verify-email", { email, code });
      if (data.token) localStorage.setItem("curate_token", data.token);
      onVerified(data.user);
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setMsg("");
    try {
      await api.post("/auth/resend-verification", { email });
      setMsg("New code sent. Check your inbox (or backend logs in dev mode).");
    } catch (err) {
      setMsg(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  useEffect(() => { inputs.current[0]?.focus(); }, []);
  useEffect(() => { if (code.length === 6) submit(); /* eslint-disable-line */ }, [code]);

  return (
    <main className="min-h-screen overflow-hidden">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-ambient" />
      <Header user={null} />
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-2xl flex-col items-center justify-center px-5 py-12 sm:px-8">
        <div data-testid="verify-card" className="curate-rise w-full rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-panel">
          <div className="grid size-12 place-items-center rounded-xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]">
            <Sparkles className="size-6" />
          </div>
          <h1 className="mt-6 font-serif text-4xl font-semibold leading-tight">Check your email</h1>
          <p className="mt-3 text-[hsl(var(--muted-foreground))]">
            We sent a 6-digit code to <span data-testid="verify-email" className="font-semibold text-[hsl(var(--foreground))]">{email}</span>. It expires in 15 minutes.
          </p>
          <form onSubmit={submit} className="mt-7">
            <div className="flex gap-2 sm:gap-3" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  data-testid={`verify-digit-${i}`}
                  ref={(el) => (inputs.current[i] = el)}
                  value={d}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKey(i, e)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  className="h-14 w-full flex-1 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--card))] text-center font-serif text-3xl font-semibold outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
                />
              ))}
            </div>
            {msg && <p data-testid="verify-error" className="mt-4 rounded-lg bg-[hsl(var(--muted))] px-4 py-3 text-sm">{msg}</p>}
            <button data-testid="verify-submit" type="submit" disabled={busy || code.length !== 6} className="btn btn-hero mt-6 w-full justify-center px-6 py-4 text-lg">
              {busy ? <Loader2 className="size-5 animate-spin" /> : "Verify and continue"}
            </button>
          </form>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
            <button data-testid="verify-resend" type="button" onClick={resend} className="font-medium text-[hsl(var(--primary))] hover:underline">Resend code</button>
            <button data-testid="verify-cancel" type="button" onClick={onCancel} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">Wrong email? Start over</button>
          </div>
        </div>
      </section>
    </main>
  );
}

function Landing({ onAuth, onNeedsVerify, onForgotPassword }) {
  const [mode, setMode] = useState("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setBusy(true);
    try {
      const path = mode === "signup" ? "/auth/register" : "/auth/login";
      const { data } = await api.post(path, { email: email.trim(), password });
      if (data.requires_verification) { onNeedsVerify(data.email); return; }
      if (data.token) localStorage.setItem("curate_token", data.token);
      onAuth(data.user);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        onNeedsVerify(detail.email);
        return;
      }
      setMsg(formatApiError(detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen overflow-hidden">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-ambient" />
      <Header user={null} />
      <section className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[1.02fr_0.98fr] lg:py-20">
        <div className="curate-rise max-w-3xl">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))]/70 px-3 py-2 text-sm text-[hsl(var(--muted-foreground))] shadow-soft">
            <Sparkles className="size-4 text-[hsl(var(--primary))]" /> Personal news digests without the noise
          </div>
          <h1 className="font-serif text-5xl font-semibold leading-[0.95] sm:text-7xl lg:text-8xl">
            Curate your news before the day curates you.
          </h1>
          <p className="mt-7 max-w-2xl text-lg leading-8 text-[hsl(var(--muted-foreground))] sm:text-xl">
            Pick the topics that matter, block outlets you don't trust, and receive a daily digest rewritten in the format you actually read.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <a data-testid="cta-trial" href="#access" className="btn btn-hero px-6 py-4 text-lg">
              Start your free trial <ChevronRight className="size-5" />
            </a>
            <a href="#pricing" className="btn btn-quiet px-6 py-4 text-lg">Compare tiers</a>
          </div>
        </div>
        <aside className="curate-rise-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]/90 p-4 shadow-panel lg:p-5">
          <div className="rounded-xl bg-digest p-5">
            <div className="flex items-center justify-between border-b border-[hsl(var(--border))] pb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">Tomorrow, 08:00</p>
                <h2 className="mt-2 font-serif text-3xl font-semibold">Your morning brief</h2>
              </div>
              <Bell className="size-5 text-[hsl(var(--primary))]" />
            </div>
            <div className="mt-5 space-y-4">
              {[
                { t: "AI chip demand reshapes cloud spending", o: "Market Pulse", c: "Cloud providers redirect capital to specialised AI infrastructure." },
                { t: "European cities trial heat-resilient streets", o: "Global Post", c: "Pilot programmes use shade corridors and lighter surfaces." },
                { t: "Small business confidence rises", o: "City Ledger", c: "Founders restart hiring as borrowing costs stabilise." },
              ].map((s) => (
                <article key={s.t} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
                  <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
                    <span>{s.o}</span>
                  </div>
                  <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
                  <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">{s.c}</p>
                </article>
              ))}
            </div>
          </div>
        </aside>
      </section>

      <section className="border-y border-[hsl(var(--border))] bg-section py-16">
        <div className="mx-auto grid max-w-7xl gap-5 px-5 sm:px-8 md:grid-cols-3">
          {[
            { Icon: ShieldOff, title: "Preference portfolio", text: "Choose interests from a smart topic list and keep specific outlets out of your feed." },
            { Icon: Wand2, title: "Readable formats", text: "Switch between tight summaries, scannable bullets, or a plain-English rewrite." },
            { Icon: Clock3, title: "Daily delivery", text: "Trial readers get a fixed morning brief. Paid readers control the time and notifications." },
          ].map(({ Icon, title, text }) => (
            <article key={title} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-soft transition-transform hover:-translate-y-1">
              <Icon className="size-6 text-[hsl(var(--primary))]" />
              <h2 className="mt-5 text-xl font-semibold">{title}</h2>
              <p className="mt-3 leading-7 text-[hsl(var(--muted-foreground))]">{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">Pricing</p>
          <h2 className="mt-3 font-serif text-4xl font-semibold sm:text-5xl">Start simple. Upgrade when timing matters.</h2>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <PricingCard testId="pricing-free" title="Free trial" price="£0" points={["5 stories per day", "Fixed 08:00 delivery", "Summary, bullet, and plain-English formats"]} />
          <PricingCard testId="pricing-plus" featured title="Curate Plus" price="£4.99/month" points={["Up to 15 stories per digest", "Custom delivery time", "Higher quality editorial rewrites", "Like/dislike to train your feed"]} />
        </div>
      </section>

      <section id="access" className="mx-auto grid max-w-7xl gap-8 px-5 pb-20 sm:px-8 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-7 shadow-panel">
          <Lock className="size-7 text-[hsl(var(--primary))]" />
          <h2 className="mt-5 font-serif text-4xl font-semibold">Create your Curate account</h2>
          <p className="mt-4 leading-7 text-[hsl(var(--muted-foreground))]">
            Your preferences, blocked outlets, delivery settings, and saved digests are kept in your private workspace.
          </p>
        </div>
        <form onSubmit={submit} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-panel">
          <div className="grid grid-cols-2 rounded-lg bg-[hsl(var(--muted))] p-1">
            <button type="button" data-testid="auth-mode-signup" onClick={() => setMode("signup")} className={mode==="signup" ? "rounded-md bg-[hsl(var(--card))] px-4 py-2 text-sm font-semibold shadow-soft" : "px-4 py-2 text-sm text-[hsl(var(--muted-foreground))]"}>Sign up</button>
            <button type="button" data-testid="auth-mode-login" onClick={() => setMode("login")} className={mode==="login" ? "rounded-md bg-[hsl(var(--card))] px-4 py-2 text-sm font-semibold shadow-soft" : "px-4 py-2 text-sm text-[hsl(var(--muted-foreground))]"}>Log in</button>
          </div>
          <label className="mt-6 block text-sm font-medium" htmlFor="email">Email</label>
          <input id="email" data-testid="auth-email" required type="email" autoComplete="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="mt-2 h-12 w-full rounded-lg border border-[hsl(var(--input))] px-4 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          <label className="mt-4 block text-sm font-medium" htmlFor="password">Password</label>
          <input id="password" data-testid="auth-password" required type="password" minLength={6} autoComplete={mode==="signup"?"new-password":"current-password"} value={password} onChange={(e)=>setPassword(e.target.value)} className="mt-2 h-12 w-full rounded-lg border border-[hsl(var(--input))] px-4 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          {msg && <p data-testid="auth-error" className="mt-4 rounded-lg bg-[hsl(var(--muted))] px-4 py-3 text-sm">{msg}</p>}
          <button data-testid="auth-submit" type="submit" disabled={busy} className="btn btn-hero mt-6 w-full justify-center px-6 py-4 text-lg">
            {busy ? "Working..." : mode==="signup" ? "Create account" : "Log in"}
          </button>
          {mode === "login" && (
            <button data-testid="forgot-password-link" type="button" onClick={() => onForgotPassword(email.trim())}
              className="mt-4 w-full text-center text-sm font-medium text-[hsl(var(--primary))] hover:underline">
              Forgot your password?
            </button>
          )}
        </form>
      </section>
    </main>
  );
}

function TopicChip({ topic, active, onClick }) {
  return (
    <button data-testid={`topic-${topic}`} type="button" onClick={onClick}
      className={active
        ? "rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-[hsl(var(--primary-foreground))]"
        : "rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-2 text-sm text-[hsl(var(--muted-foreground))] transition-colors hover:text-[hsl(var(--foreground))]"}>
      {topic}
    </button>
  );
}

function Onboarding({ portfolio, onUpdate, onContinue }) {
  const [topic, setTopic] = useState("");
  const [outlet, setOutlet] = useState("");
  const selected = portfolio.topics || [];
  const blocked = portfolio.blocked_outlets || [];

  const toggleTopic = (t) => {
    const next = selected.some(s => s.toLowerCase() === t.toLowerCase())
      ? selected.filter(s => s.toLowerCase() !== t.toLowerCase()) : [...selected, t];
    onUpdate({ topics: next.length ? next : [t] });
  };
  const addTopic = (e) => { e.preventDefault(); const t = topic.trim(); if (!t) return;
    if (!selected.some(s => s.toLowerCase() === t.toLowerCase())) onUpdate({ topics: [...selected, t] });
    setTopic("");
  };
  const addBlocked = (e) => { e.preventDefault(); const o = outlet.trim(); if (!o) return;
    if (!blocked.some(b => b.toLowerCase() === o.toLowerCase())) onUpdate({ blocked_outlets: [...blocked, o] });
    setOutlet("");
  };
  const removeBlocked = (o) => onUpdate({ blocked_outlets: blocked.filter(b => b !== o) });

  return (
    <section className="mx-auto grid max-w-5xl gap-6 px-5 py-10 sm:px-8 lg:grid-cols-[0.9fr_1.1fr]">
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-7 shadow-panel">
        <Sparkles className="size-7 text-[hsl(var(--primary))]" />
        <h1 className="mt-5 font-serif text-4xl font-semibold">Set up your preference portfolio</h1>
        <p className="mt-4 leading-7 text-[hsl(var(--muted-foreground))]">Pick topics you care about. Block outlets you don't want included.</p>
        <button data-testid="onboarding-continue" disabled={!selected.length} onClick={onContinue} className="btn btn-hero mt-7 px-6 py-4 text-lg">
          Continue to Curate <ChevronRight className="size-5" />
        </button>
      </div>
      <div className="space-y-6">
        <article className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-soft">
          <h2 className="text-xl font-semibold">Topics</h2>
          <form onSubmit={addTopic} className="mt-4 flex gap-2">
            <input data-testid="topic-input" value={topic} onChange={(e)=>setTopic(e.target.value)} placeholder="Add a custom topic" className="h-11 min-w-0 flex-1 rounded-lg border border-[hsl(var(--input))] px-3 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
            <button data-testid="topic-add" type="submit" className="btn btn-quiet px-4 py-2">Add</button>
          </form>
          <div className="mt-5 flex flex-wrap gap-2">
            {[...new Set([...selected, ...STARTER_TOPICS])].map(t => (
              <TopicChip key={t} topic={t} active={selected.some(s=>s.toLowerCase()===t.toLowerCase())} onClick={()=>toggleTopic(t)} />
            ))}
          </div>
        </article>
        <article className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-soft">
          <h2 className="text-xl font-semibold">Blocked outlets</h2>
          <p className="mt-2 text-sm font-semibold text-[hsl(var(--destructive))]">These outlets will NOT appear in your digest</p>
          <form onSubmit={addBlocked} className="mt-4 flex gap-2">
            <input data-testid="blocked-input" value={outlet} onChange={(e)=>setOutlet(e.target.value)} placeholder="Outlet name" className="h-11 min-w-0 flex-1 rounded-lg border border-[hsl(var(--input))] px-3 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
            <button data-testid="blocked-add" type="submit" className="btn btn-quiet px-4 py-2">Add to block list</button>
          </form>
          <div className="mt-4 flex flex-wrap gap-2">
            {blocked.length ? blocked.map(o => (
              <button key={o} type="button" onClick={()=>removeBlocked(o)} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm font-medium text-[hsl(var(--destructive))] line-through decoration-2">
                {o} <X className="size-3" />
              </button>
            )) : <p className="text-sm text-[hsl(var(--muted-foreground))]">No outlets blocked. All outlets are allowed by default.</p>}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {STARTER_OUTLETS.filter(o => !blocked.some(b=>b.toLowerCase()===o.toLowerCase())).map(o => (
              <button key={o} type="button" onClick={()=>onUpdate({ blocked_outlets: [...blocked, o] })} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">
                {o} <ShieldOff className="size-3" />
              </button>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

const UP_REASONS = ["Relevant to my interests", "Well written", "Important story", "Other"];
const DOWN_REASONS = ["Not relevant to me", "Don't like this source", "Topic not interesting", "Already seen this", "Other"];

function FeedbackPopover({ kind, onPick, onClose }) {
  const reasons = kind === "up" ? UP_REASONS : DOWN_REASONS;
  return (
    <div data-testid={`feedback-popover-${kind}`} className="mt-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 shadow-panel curate-rise">
      <div className="flex items-center justify-between px-1 pb-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
          {kind === "up" ? "Why did you like this?" : "Why didn't you like this?"}
        </p>
        <button data-testid={`feedback-close-${kind}`} type="button" onClick={onClose} aria-label="Close" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"><X className="size-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {reasons.map((r) => (
          <button key={r} data-testid={`reason-${r}`} type="button" onClick={() => onPick(r)} className="btn btn-quiet rounded-full px-3 py-1.5 text-xs">
            {r}
          </button>
        ))}
      </div>
    </div>
  );
}

function Reader({ items, onBack, onFeedback }) {
  const [openFor, setOpenFor] = useState(null); // { itemId, kind }

  const handleClick = (id, kind) => setOpenFor({ itemId: id, kind });
  const handlePick = async (reason) => {
    if (!openFor) return;
    await onFeedback(openFor.itemId, openFor.kind, reason);
    setOpenFor(null);
  };
  const close = () => setOpenFor(null);

  return (
    <section className="min-h-[calc(100vh-4rem)] px-5 py-6 sm:px-8">
      <div className="sticky top-16 z-20 -mx-5 mb-2 border-b border-[hsl(var(--border))]/70 bg-[hsl(var(--background))]/85 px-5 py-3 backdrop-blur-xl sm:-mx-8 sm:px-8">
        <div className="mx-auto max-w-3xl">
          <button data-testid="reader-back" onClick={onBack} className="btn btn-quiet px-4 py-2 text-sm">
            <ArrowLeft className="size-4" /> Back to dashboard
          </button>
        </div>
      </div>
      <div className="mx-auto max-w-3xl">
        <header className="border-b border-[hsl(var(--border))] py-10">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">{formatBriefDate(items[0]?.created_at)}</p>
          <h1 className="mt-3 font-serif text-5xl font-semibold">Today's digest</h1>
          <p className="mt-4 text-lg leading-8 text-[hsl(var(--muted-foreground))]">A clean reading edition of the stories selected for your portfolio.</p>
        </header>
        <div className="divide-y divide-[hsl(var(--border))]">
          {items.map((it) => {
            const popoverOpen = openFor && openFor.itemId === it.id;
            return (
              <article key={it.id} data-testid={`story-${it.id}`} className="py-9">
                <div className="flex flex-wrap items-center gap-3 text-sm text-[hsl(var(--muted-foreground))]">
                  <span className="font-semibold text-[hsl(var(--primary))]">{it.outlet}</span>
                  <span>{it.topic}</span>
                  {it.why_picked && (
                    <span data-testid={`why-picked-${it.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))]/10 px-3 py-1 text-xs font-medium text-[hsl(var(--primary))]">
                      <Sparkles className="size-3" /> {it.why_picked}
                    </span>
                  )}
                </div>
                <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight">{it.title}</h2>
                <p className="mt-5 whitespace-pre-line text-lg leading-8 text-[hsl(var(--foreground))]/85">{it.content}</p>
                <div className="mt-6 flex items-center gap-2">
                  <button data-testid={`thumbs-up-${it.id}`} onClick={()=>handleClick(it.id,"up")} aria-label="Thumbs up" className={`btn px-3 py-2 rounded-lg ${it.feedback==="up"?"btn-hero":"btn-quiet"}`}><ThumbsUp className="size-4" /></button>
                  <button data-testid={`thumbs-down-${it.id}`} onClick={()=>handleClick(it.id,"down")} aria-label="Thumbs down" className={`btn px-3 py-2 rounded-lg ${it.feedback==="down"?"btn-hero":"btn-quiet"}`}><ThumbsDown className="size-4" /></button>
                  {it.feedback_reason && (
                    <span data-testid={`feedback-reason-${it.id}`} className="ml-2 text-xs text-[hsl(var(--muted-foreground))]">— {it.feedback_reason}</span>
                  )}
                  {it.source_url && (
                    <a data-testid={`source-${it.id}`} href={it.source_url} target="_blank" rel="noreferrer" className="btn btn-quiet px-3 py-2 text-sm rounded-lg ml-auto">
                      Read source <ExternalLink className="size-4" />
                    </a>
                  )}
                </div>
                {popoverOpen && (
                  <FeedbackPopover kind={openFor.kind} onPick={handlePick} onClose={close} />
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function AdminPanel({ onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data.users || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleTier = async (u) => {
    setBusyId(u.id); setErr("");
    const next = u.tier === "paid" ? "trial" : "paid";
    try {
      await api.post(`/admin/users/${u.id}/tier`, { tier: next });
      setUsers((prev) => prev.map(x => x.id === u.id ? { ...x, tier: next } : x));
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusyId(""); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThis removes their account, portfolio, digest, and feedback. If they have an active Curate Plus subscription it will also be cancelled in Stripe.\n\nThis cannot be undone.`)) return;
    setBusyId(u.id); setErr("");
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter(x => x.id !== u.id));
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusyId(""); }
  };

  return (
    <article data-testid="admin-panel" className="rounded-xl border border-[hsl(var(--primary))]/40 bg-premium p-6 shadow-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">Admin</p>
          <h2 className="mt-2 font-serif text-2xl font-semibold">User management</h2>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Toggle any user between Free trial and Curate Plus.</p>
        </div>
        <button data-testid="admin-refresh" onClick={load} className="btn btn-quiet px-3 py-2 text-sm"><RefreshCw className="size-4" /> Refresh</button>
      </div>
      {err && <p data-testid="admin-error" className="mt-4 rounded-lg bg-[hsl(var(--destructive))]/10 px-4 py-2 text-sm text-[hsl(var(--destructive))]">{err}</p>}
      <div className="mt-5 divide-y divide-[hsl(var(--border))] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        {loading ? (
          <div className="flex items-center gap-2 p-5 text-sm text-[hsl(var(--muted-foreground))]"><Loader2 className="size-4 animate-spin" /> Loading users...</div>
        ) : users.length === 0 ? (
          <p className="p-5 text-sm text-[hsl(var(--muted-foreground))]">No users yet.</p>
        ) : users.map(u => (
          <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">{u.email}{u.role === "admin" && <span className="ml-2 rounded bg-[hsl(var(--primary))]/15 px-2 py-0.5 text-xs font-semibold text-[hsl(var(--primary))]">admin</span>}</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Tier: <span data-testid={`admin-user-tier-${u.id}`} className={u.tier === "paid" ? "font-semibold text-[hsl(var(--primary))]" : ""}>{u.tier === "paid" ? "Curate Plus" : "Free trial"}</span></p>
            </div>
            <button data-testid={`admin-toggle-${u.id}`} disabled={busyId === u.id || u.role === "admin"} onClick={() => toggleTier(u)} className={`btn px-4 py-2 text-sm rounded-lg ${u.tier === "paid" ? "btn-quiet" : "btn-hero"}`}>
              {busyId === u.id ? <Loader2 className="size-4 animate-spin" /> : u.tier === "paid" ? "Make Trial" : "Make Plus"}
            </button>
            {u.role !== "admin" && (
              <button data-testid={`admin-delete-${u.id}`} disabled={busyId === u.id} onClick={() => deleteUser(u)}
                className="btn px-3 py-2 text-sm rounded-lg text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10" aria-label={`Delete ${u.email}`}>
                <Trash2 className="size-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </article>
  );
}

function Dashboard({ user, portfolio, items, onUpdate, onGenerate, onRefresh, onRead, onUpgrade, onCancelSub, busy, message }) {
  const [topic, setTopic] = useState("");
  const [outlet, setOutlet] = useState("");
  const tier = user.tier || "trial";
  const fmt = portfolio.digest_format || "summary";
  const isPaid = tier === "paid";

  const toggleTopic = (t) => {
    const sel = portfolio.topics || [];
    const next = sel.some(s => s.toLowerCase() === t.toLowerCase())
      ? sel.filter(s => s.toLowerCase() !== t.toLowerCase()) : [...sel, t];
    onUpdate({ topics: next.length ? next : [t] });
  };
  const addTopic = (e) => { e.preventDefault(); const t = topic.trim(); if (!t) return;
    const sel = portfolio.topics || [];
    if (!sel.some(s => s.toLowerCase() === t.toLowerCase())) onUpdate({ topics: [...sel, t] });
    setTopic("");
  };
  const addBlocked = (e) => { e.preventDefault(); const o = outlet.trim(); if (!o) return;
    const bl = portfolio.blocked_outlets || [];
    if (!bl.some(b => b.toLowerCase() === o.toLowerCase())) onUpdate({ blocked_outlets: [...bl, o] });
    setOutlet("");
  };

  return (
    <section className="mx-auto max-w-7xl space-y-6 px-5 py-8 sm:px-8" data-testid="dashboard">
      {/* Top row: digest settings + brief-ready card */}
      <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
        <article className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-panel">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">Digest settings</p>
              <h1 className="mt-2 font-serif text-4xl font-semibold">Today's personalised brief</h1>
            </div>
            <div className="flex gap-2">
              {items.length > 0 && isPaid && (
                <button data-testid="refresh-digest-btn" onClick={onRefresh} disabled={busy} className="btn btn-quiet px-4 py-3">
                  <RefreshCw className={`size-4 ${busy?"animate-spin":""}`} /> Refresh
                </button>
              )}
              <button data-testid="generate-digest-btn" onClick={onGenerate} disabled={busy} className="btn btn-hero px-5 py-3">
                {busy ? <Loader2 className="size-4 animate-spin" /> : <FileText className="size-4" />} {busy?"Curating...":"Generate digest"}
              </button>
            </div>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg bg-[hsl(var(--muted))] p-4">
              <label className="text-sm font-medium" htmlFor="time">Delivery time</label>
              <input id="time" data-testid="delivery-time" type="time" disabled={!isPaid} value={portfolio.delivery_time || "08:00"} onChange={(e)=>onUpdate({ delivery_time: e.target.value })} className="mt-2 h-11 w-full rounded-lg border border-[hsl(var(--input))] px-3 disabled:opacity-60" />
              <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{isPaid ? "Custom timing enabled." : "Fixed on trial. Upgrade to choose."}</p>
            </div>
            <div className="rounded-lg bg-[hsl(var(--muted))] p-4">
              <p className="text-sm font-medium">Format</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {Object.keys(FORMAT_LABELS).map(f => (
                  <button key={f} data-testid={`format-${f}`} type="button" onClick={()=>onUpdate({ digest_format: f })}
                    className={fmt===f ? "rounded-md bg-[hsl(var(--card))] px-3 py-2 text-xs font-semibold shadow-soft" : "rounded-md px-3 py-2 text-xs text-[hsl(var(--muted-foreground))]"}>
                    {FORMAT_LABELS[f]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </article>

        <div className="space-y-4">
          {message && <p data-testid="dash-message" className="rounded-lg bg-[hsl(var(--muted))] px-4 py-3 text-sm">{message}</p>}
          {items.length ? (
            <article data-testid="brief-ready-card" className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-soft">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[hsl(var(--primary))]">{formatBriefDate(items[0]?.created_at)}</p>
              <h2 className="mt-3 font-serif text-3xl font-semibold">Your brief is ready</h2>
              <p className="mt-3 text-[hsl(var(--muted-foreground))]">{items.length} stories prepared from your preference portfolio.</p>
              <button data-testid="read-digest-btn" onClick={onRead} className="btn btn-hero mt-6 px-5 py-3">Read your digest</button>
            </article>
          ) : (
            <article className="rounded-xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 text-center shadow-soft">
              <Sparkles className="mx-auto size-8 text-[hsl(var(--primary))]" />
              <h2 className="mt-4 text-xl font-semibold">No digest yet</h2>
              <p className="mt-2 text-[hsl(var(--muted-foreground))]">Generate your first personalised brief from your topic portfolio.</p>
            </article>
          )}
        </div>
      </div>

      {/* Admin panel (admins only) */}
      {user.role === "admin" && <AdminPanel />}

      {/* Bottom row: profile, portfolio, blocked outlets */}
      <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="space-y-6">
          <article className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-soft">
            <div className="flex items-center gap-3">
              <div className="grid size-11 place-items-center rounded-lg bg-[hsl(var(--muted))]"><UserRound className="size-5 text-[hsl(var(--primary))]" /></div>
              <div>
                <p className="font-semibold" data-testid="user-email">{user.email}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="user-tier">{isPaid ? "Curate Plus" : "Free trial"}</p>
              </div>
            </div>
            {!isPaid && (
              <button data-testid="upgrade-btn" onClick={onUpgrade} className="btn btn-hero mt-5 w-full justify-center px-4 py-3">
                <Sparkles className="size-4" /> Upgrade to Curate Plus — £4.99/mo
              </button>
            )}
            {isPaid && user.role !== "admin" && (
              <button data-testid="cancel-sub-btn" onClick={onCancelSub} className="btn btn-quiet mt-5 w-full justify-center px-4 py-3 text-sm">
                Cancel subscription
              </button>
            )}
          </article>

          <article className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-soft">
            <h2 className="text-xl font-semibold">Preference portfolio</h2>
            <form onSubmit={addTopic} className="mt-4 flex gap-2">
              <input data-testid="dash-topic-input" value={topic} onChange={(e)=>setTopic(e.target.value)} placeholder="Add a custom topic" className="h-11 min-w-0 flex-1 rounded-lg border border-[hsl(var(--input))] px-3 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
              <button data-testid="dash-topic-add" type="submit" className="btn btn-quiet px-4 py-2">Add</button>
            </form>
            <div className="mt-5 flex flex-wrap gap-2">
              {[...new Set([...(portfolio.topics||[]), ...STARTER_TOPICS])].map(t => (
                <TopicChip key={t} topic={t} active={(portfolio.topics||[]).some(s=>s.toLowerCase()===t.toLowerCase())} onClick={()=>toggleTopic(t)} />
              ))}
            </div>
          </article>
        </div>

        <article className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 shadow-soft">
          <h2 className="text-xl font-semibold">Blocked outlets</h2>
          <p className="mt-2 text-sm font-semibold text-[hsl(var(--destructive))]">These outlets will NOT appear in your digest</p>
          <form onSubmit={addBlocked} className="mt-4 flex gap-2">
            <input data-testid="dash-blocked-input" value={outlet} onChange={(e)=>setOutlet(e.target.value)} placeholder="Outlet name" className="h-11 min-w-0 flex-1 rounded-lg border border-[hsl(var(--input))] px-3 outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
            <button data-testid="dash-blocked-add" type="submit" className="btn btn-quiet px-4 py-2">Add to block list</button>
          </form>
          <div className="mt-4 flex flex-wrap gap-2">
            {(portfolio.blocked_outlets||[]).length ? (portfolio.blocked_outlets||[]).map(o => (
              <button key={o} type="button" onClick={()=>onUpdate({ blocked_outlets: (portfolio.blocked_outlets||[]).filter(b=>b!==o) })} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm font-medium text-[hsl(var(--destructive))] line-through decoration-2">
                {o} <X className="size-3" />
              </button>
            )) : <p className="text-sm text-[hsl(var(--muted-foreground))]">No outlets blocked. All outlets allowed.</p>}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {STARTER_OUTLETS.filter(o => !(portfolio.blocked_outlets||[]).some(b=>b.toLowerCase()===o.toLowerCase())).slice(0,20).map(o => (
              <button key={o} type="button" onClick={()=>onUpdate({ blocked_outlets: [...(portfolio.blocked_outlets||[]), o] })} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">
                {o} <ShieldOff className="size-3" />
              </button>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState({ topics: [], blocked_outlets: [], delivery_time: "08:00", digest_format: "summary" });
  const [items, setItems] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showReader, setShowReader] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState(null);
  const [showForgot, setShowForgot] = useState(null); // null | string (preset email)

  const loadWorkspace = useCallback(async () => {
    try {
      const [pRes, dRes] = await Promise.all([api.get("/portfolio"), api.get("/digest")]);
      setPortfolio(pRes.data);
      setItems(dRes.data.items || []);
    } catch (e) {
      console.warn("loadWorkspace failed:", e?.response?.data || e?.message);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        await loadWorkspace();
        // Onboarding flag per user
        const onboarded = localStorage.getItem(`curate:onboarded:${data.user.id}`) === "true";
        if (!onboarded) setShowOnboarding(true);
      } catch (e) {
        setUser(null);
      } finally { setLoading(false); }
    })();
  }, [loadWorkspace]);

  // Handle Stripe success redirect
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "success") {
      const session_id = params.get("session_id");
      api.post("/billing/upgrade-confirm", session_id ? { session_id } : {}).then(({ data }) => {
        setUser(data.user);
        setMessage("Welcome to Curate Plus! Enjoy unlimited refreshes and richer digests.");
      }).catch((e) => {
        setMessage(formatApiError(e.response?.data?.detail) || "Could not confirm upgrade.");
      });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("upgrade") === "cancel") {
      setMessage("Upgrade cancelled — you can subscribe whenever you're ready.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [user]);

  const handleAuth = async (u) => {
    setPendingVerifyEmail(null);
    setUser(u);
    await loadWorkspace();
    const onboarded = localStorage.getItem(`curate:onboarded:${u.id}`) === "true";
    if (!onboarded) setShowOnboarding(true);
  };

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout call failed:", e?.message); }
    localStorage.removeItem("curate_token");
    setUser(null); setPortfolio({ topics: [], blocked_outlets: [], delivery_time: "08:00", digest_format: "summary" });
    setItems([]); setShowOnboarding(false); setShowReader(false);
  };

  const updatePortfolio = async (patch) => {
    try {
      const { data } = await api.put("/portfolio", patch);
      setPortfolio(data);
      if (patch.tier || patch.digest_format) {
        const me = await api.get("/auth/me"); setUser(me.data.user);
      }
    } catch (e) { setMessage(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const completeOnboarding = () => {
    if (user) localStorage.setItem(`curate:onboarded:${user.id}`, "true");
    setShowOnboarding(false);
  };

  const generate = async () => {
    setBusy(true); setMessage("");
    try {
      await api.post("/digest/generate");
      const { data } = await api.get("/digest");
      setItems(data.items || []);
      setShowReader(false);
      setMessage(`Fresh digest ready — ${(data.items||[]).length} stories.`);
    } catch (e) { setMessage(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const upgrade = async () => {
    try {
      const { data } = await api.post("/billing/checkout");
      if (data.url) window.location.href = data.url;
    } catch (e) { setMessage(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your Curate Plus subscription? You'll keep access until the end of the current billing period.")) return;
    try {
      const { data } = await api.post("/billing/cancel");
      setUser(data.user);
      setMessage(data.message || "Subscription cancelled. You'll keep Plus access until the end of your billing period.");
    } catch (e) { setMessage(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const giveFeedback = async (id, value, reason = null) => {
    try {
      await api.post("/digest/feedback", { digest_item_id: id, feedback: value, reason });
      setItems((prev) => prev.map(it => it.id === id ? { ...it, feedback: value, feedback_reason: reason } : it));
    } catch (e) {
      console.warn("feedback failed:", e?.response?.data || e?.message);
    }
  };

  if (loading) return (
    <main className="min-h-screen flex items-center justify-center bg-ambient">
      <Loader2 className="size-8 animate-spin text-[hsl(var(--primary))]" />
    </main>
  );

  if (pendingVerifyEmail && !user) {
    return <VerifyEmail email={pendingVerifyEmail} onVerified={handleAuth} onCancel={() => setPendingVerifyEmail(null)} />;
  }

  if (showForgot !== null && !user) {
    return <ForgotPassword presetEmail={showForgot} onDone={handleAuth} onCancel={() => setShowForgot(null)} />;
  }

  if (!user) return <Landing onAuth={handleAuth} onNeedsVerify={setPendingVerifyEmail} onForgotPassword={setShowForgot} />;

  return (
    <main className="min-h-screen">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-ambient" />
      <Header user={user} onLogout={handleLogout} />
      {showOnboarding ? (
        <Onboarding portfolio={portfolio} onUpdate={updatePortfolio} onContinue={completeOnboarding} />
      ) : showReader ? (
        <Reader items={items} onBack={()=>setShowReader(false)} onFeedback={giveFeedback} />
      ) : (
        <Dashboard user={user} portfolio={portfolio} items={items}
          onUpdate={updatePortfolio} onGenerate={generate} onRefresh={generate}
          onRead={()=>setShowReader(true)} onUpgrade={upgrade} onCancelSub={cancelSubscription}
          busy={busy} message={message} />
      )}
    </main>
  );
}
